import { Navigation, MapPin, Bus, Bike, Recycle, AlertTriangle, ArrowRight, ChevronRight, Store, Plus, Phone, ShoppingCart, Bell, Car } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

import { useState, useEffect } from 'react';
import { getLocation } from '../../lib/geo';
import { openDirections } from '../../lib/utils';
import { arrivalLabel, isEstimate } from '../../lib/busArrivals';
import { freshnessOf } from '../../lib/freshness';

// Great-circle distance in km. Raw degree differences overstate east–west distance by about a
// third at this latitude, which skewed both the choice of nearest place and its walking time.
function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function TuBarrioSection({ userLocation, approximate = false, setUserLocation, nearestBusStop, nearestBusArrivals, nearestBike, nearestBins, nearestSupermarket, farmaciaGuardia, farmaciasData, recentIncidencias, stats, navigate, upcoming, bikeStationCount, liveTrains, nextBusSantander, user, onShowAuth, eraLive }) {
  const [locationDenied, setLocationDenied] = useState(window.__nativePermissions?.location === 'denied');

  // Listen for native permission changes (after returning from Settings)
  useEffect(() => {
    const handler = () => {
      const newStatus = window.__nativePermissions?.location;
      setLocationDenied(newStatus === 'denied');
      // Location arrival after a grant is handled by Dashboard's subscribeLocation()
    };
    window.addEventListener('nativePermissionsChanged', handler);
    return () => window.removeEventListener('nativePermissionsChanged', handler);
  }, []);
  if (!userLocation && !recentIncidencias.length && (!upcoming || upcoming.length === 0)) return null;

  return (
    <div data-testid="tu-barrio-section">
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-1.5">
          <MapPin size={12} className="text-indigo-600" aria-hidden="true" />
          {/* Not «En vivo»: the pharmacy, supermarket and bins nearby are directories; only the bus
              times below are live, and they say so themselves. */}
          <span className="text-[11px] font-bold text-indigo-600 uppercase tracking-wider">Lo más cercano</span>
        </span>
        <button onClick={() => navigate('/mapa')} className="text-xs text-indigo-600 font-bold hover:underline flex items-center gap-1" data-testid="tu-barrio-see-map">
          Ver mapa <ArrowRight size={12} />
        </button>
      </div>

      {approximate && (
        <p className="text-xs text-slate-500 font-semibold -mt-1.5 mb-2.5" data-testid="tu-barrio-approximate">
          Cerca de {userLocation?.barrio || 'Centro'} · ubicación aproximada
        </p>
      )}

      {/* GPS prompt. Shown while the place is the barrio's centre rather than the reader's. */}
      {approximate && (
        <div className="card-solid p-4 mb-3 flex flex-wrap items-center gap-3 border-dashed border-indigo-200 bg-indigo-50/30" data-testid="tu-barrio-gps-prompt">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
            <MapPin size={18} className="text-indigo-500" />
          </div>
          <div className="flex-1 min-w-[170px]">
            {/* In the app "denied" does not mean blocked: Android reports it after a single "No
                permitir" (and, per the shell's own notes, before the first question), when the
                system can still ask. This used to say "Has bloqueado la ubicación" and hide
                Activar. Activar is kept: the shell's tap handler asks when it can and opens
                Ajustes when it cannot (SharedWebView REQUEST_LOCATION with fresh). In a browser
                a denial is the browser's to undo, so that case keeps its instructions. */}
            {locationDenied && !window.__isNativeApp ? (
              <>
                <p className="text-sm font-bold text-torre-slate">Ubicación desactivada</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Has bloqueado la ubicación. Actívala en los ajustes de tu navegador y recarga la página.
                </p>
              </>
            ) : locationDenied ? (
              <>
                <p className="text-sm font-bold text-torre-slate">Ubicación desactivada</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Toca Activar para ver lo más cercano a ti. Si la bloqueaste, te llevamos a Ajustes.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-bold text-torre-slate">Activa tu ubicación</p>
                <p className="text-xs text-slate-500 mt-0.5">Ahora ves lo que hay cerca de {userLocation?.barrio || 'Centro'}. Con tu ubicación, lo más cercano a ti y a cuántos minutos.</p>
              </>
            )}
          </div>
          {(!locationDenied || window.__isNativeApp) && (
            <Button size="sm" onClick={() => {
              // Dashboard also subscribes to the shared geo helper, so this resolves
              // there too even if this component unmounts before the fix lands.
              getLocation({ fresh: true }).then((loc) => {
                if (loc?.lat && loc?.lon) setUserLocation(loc);
                else if (!window.__isNativeApp) setLocationDenied(true); // native: permission listener handles denial state
              });
            }} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-full h-8 px-3 font-bold shrink-0 ml-auto" data-testid="tu-barrio-activate-gps">
              <Navigation size={12} className="mr-1" /> Activar
            </Button>
          )}
        </div>
      )}

      {/* Main live cards.
          Columns, not a grid. As a grid (`sm:grid-cols-2 lg:grid-cols-3 items-start`) each row
          started under its tallest card: at 1440px the farmacia card (344px, with the guardia
          one inside) pushed the second row 110-230px below the bus and Torrebici cards, which
          left two large holes (Tanuj, 2026-09-25: "stacks weirdly"). Stretching rows instead
          left short cards mostly blank. CSS columns let each card sit right under the one
          above it; source order (what screen readers and the tab key follow) is unchanged. */}
      {userLocation && (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-2.5 sm:gap-3 [&>*]:break-inside-avoid [&>*]:w-full [&>*]:mb-2.5 sm:[&>*]:mb-3" data-testid="tu-barrio-cards">
          {/* Nearest bus stop */}
          {nearestBusStop && (
            <div className="card-solid p-3 sm:p-4 text-left" data-testid="tu-barrio-bus">
              {/* The name block is the link; «Cómo llegar» sits beside it, not inside it. It was a
                  button nested in the card's button: invalid HTML, and unreliable for keyboard and
                  screen-reader users (axe nested-interactive, 2026-09-24). */}
              <div className="flex items-start gap-2.5 sm:gap-3 w-full">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                  <Bus size={16} className="text-torre-teal" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <button onClick={() => navigate(`/torrebus?parada=${nearestBusStop.codigo}`)} className="w-full flex items-start gap-2 text-left group" data-testid="tu-barrio-bus-open">
                    <span className="flex-1 min-w-0">
                      <span className="block text-[11px] sm:text-xs font-extrabold text-torre-slate truncate">Parada más cercana</span>
                      <span className="block text-xs sm:text-sm font-bold text-torre-teal mt-0.5 truncate">{nearestBusStop.nombre || nearestBusStop.ref || 'Parada'}</span>
                      <span className="block text-[11px] sm:text-[11px] text-slate-400">{approximate ? '' : `${nearestBusStop.walkMin} min · `}Parada {nearestBusStop.codigo}</span>
                    </span>
                    <ArrowRight size={14} className="text-slate-500 group-hover:text-torre-teal shrink-0 transition-colors" aria-hidden="true" />
                  </button>
                  {nearestBusStop.lat && nearestBusStop.lon && (
                    <button onClick={() => openDirections(nearestBusStop.lat, nearestBusStop.lon, 'walking')}
                      className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-xl text-[11px] font-bold bg-teal-50 text-torre-teal border border-teal-100 hover:bg-teal-100 transition-colors text-left"
                      data-testid="tu-barrio-bus-directions">
                      <Navigation size={10} aria-hidden="true" /> Cómo llegar
                    </button>
                  )}
                </div>
              </div>
              <div className="ml-[42px] sm:ml-[52px]">
                {nearestBusArrivals.length > 0 ? (
                  <div className="space-y-1.5 mt-2.5">
                    {/* «Estimación superior a 90 minutos» used to read «Sin servicio próximo»; stale
                        copies (no `tiempo`) say nothing rather than old minutes. lib/busArrivals. */}
                    {nearestBusArrivals.map((arr, i) => (
                      <div key={`${arr.linea}-${arr.destino}-${i}`} className="py-1.5 px-2 sm:px-2.5 rounded-lg bg-teal-50/50 border border-teal-100/60 flex items-center justify-between gap-1"
                        data-testid={`tu-barrio-arrival-${i}`}>
                        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                          <Badge className="bg-torre-teal/10 text-torre-teal border-torre-teal/20 text-[11px] font-extrabold rounded-md px-1.5 shrink-0">{arr.linea}</Badge>
                          {arr.destino && <span className="text-[11px] sm:text-xs text-slate-500 truncate">{arr.destino}</span>}
                        </div>
                        <span className={`text-xs sm:text-sm font-extrabold tabular-nums shrink-0 ml-1 ${!isEstimate(arr) ? 'text-slate-500' : arr.segundos < 120 ? 'text-emerald-600' : 'text-torre-teal'}`}>
                          {arrivalLabel(arr) ?? 'Sin datos recientes'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-2.5">Sin llegadas previstas</p>
                )}
              </div>
            </div>
          )}

          {/* Nearest bike station */}
          {nearestBike && (
            <button onClick={() => navigate('/torrebici')} className="card-solid p-3 sm:p-4 text-left group" data-testid="tu-barrio-bike">
              <div className="flex items-start gap-2.5 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-cyan-50 flex items-center justify-center shrink-0">
                  <Bike size={16} className="text-cyan-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] sm:text-xs font-extrabold text-torre-slate truncate">Torrebici más cercana</p>
                  <p className="text-xs sm:text-sm font-bold text-cyan-600 mt-0.5 truncate">{nearestBike.lugar || nearestBike.ref || 'Estación'}</p>
                  <p className="text-[11px] sm:text-[11px] text-slate-400">{[approximate ? null : `${nearestBike.walkMin} min`, nearestBike.barrio, nearestBike.lugar ? nearestBike.ref : null].filter(Boolean).join(' · ')}</p>
                </div>
                <ArrowRight size={14} className="text-slate-500 group-hover:text-cyan-600 shrink-0 transition-colors" />
              </div>
            </button>
          )}

          {/* Nearest pharmacy + 24H pharmacy */}
          {(() => {
            const allFarmacias = farmaciasData?.farmacias || [];
            
            // Find nearest pharmacy by distance, from the full list (farmaciasData), which has the address
            let nearest = null;
            if (userLocation && allFarmacias.length > 0) {
              let minDist = Infinity;
              for (const f of allFarmacias) {
                if (f.lat && f.lon) {
                  const d = haversineKm(userLocation.lat, userLocation.lon, f.lat, f.lon);
                  if (d < minDist) { minDist = d; nearest = f; }
                }
              }
              if (nearest) {
                nearest = { ...nearest, walkMin: Math.max(1, Math.round(minDist * 1000 / 80)) };
              }
            }

            // Find the 24H pharmacy (Farmacia de Guardia from COF)
            const guardia = farmaciaGuardia?.farmacias?.[0];

            if (!nearest && !guardia) return null;

            return (
              <div className="card-solid p-3 sm:p-4 text-left" data-testid="tu-barrio-farmacia">
                {/* Nearest pharmacy */}
                {nearest && (
                  <div className="flex items-start gap-2.5 sm:gap-3 w-full">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                      <Plus size={16} className="text-emerald-600" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      {/* Name block is the link; Llamar and Cómo llegar are siblings (see the bus card). */}
                      <button onClick={() => navigate('/farmacias')} className="w-full text-left" data-testid="tu-barrio-farmacia-open">
                        <span className="block text-[11px] sm:text-xs font-extrabold text-torre-slate truncate">Farmacia más cercana</span>
                        <span className="block text-xs sm:text-sm font-bold text-emerald-600 mt-0.5 truncate">{nearest.nombre}</span>
                        <span className="block text-[11px] sm:text-[11px] text-slate-400 truncate">{approximate ? '' : `${nearest.walkMin} min andando · `}{(nearest.direccion || '').split(',')[0]}</span>
                      </button>
                      {/* The row wraps, so a pill that does not fit moves whole to a second row: «Cómo llegar» broke
                          into two lines at 320 px in Modo Sénior (UI check, 7 Oct 2026). rounded-xl, not a pill: at
                          200 % text a label wider than its whole column wraps as a box, not a stretched oval. */}
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {nearest.telefono && (
                          <a href={`tel:${nearest.telefono.replace(/\s/g, '')}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-xl text-[11px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100 hover:bg-emerald-100 transition-colors"
                            data-testid="tu-barrio-farmacia-call">
                            <Phone size={10} /> Llamar
                          </a>
                        )}
                        {nearest.lat && nearest.lon && (
                          <button onClick={() => openDirections(nearest.lat, nearest.lon, 'walking')}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-xl text-[11px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100 hover:bg-emerald-100 transition-colors"
                            data-testid="tu-barrio-farmacia-directions">
                            <Navigation size={10} aria-hidden="true" /> Cómo llegar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {/* 24H / Guard pharmacy — show if different from nearest */}
                {guardia && (!nearest || guardia.titular?.toLowerCase() !== nearest.nombre?.toLowerCase()) && (
                  <div className={`flex items-start gap-2.5 sm:gap-3 ${nearest ? 'mt-3 pt-3 border-t border-slate-100' : ''}`}>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                      <Plus size={16} className="text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-1.5">
                        <p className="text-[11px] sm:text-xs font-extrabold text-torre-slate truncate">Farmacia de guardia</p>
                        <span className="text-[11px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full shrink-0">24h</span>
                      </div>
                      <p className="text-xs sm:text-sm font-bold text-amber-600 mt-0.5 truncate">{guardia.titular?.split(',').map(s => s.trim()).reverse().join(' ')}</p>
                      <p className="text-[11px] sm:text-[11px] text-slate-400 truncate">{guardia.direccion}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {guardia.telefono && (
                          <a href={`tel:${guardia.telefono.replace(/\s/g, '')}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-xl text-[11px] font-bold bg-amber-50 text-amber-600 border border-amber-100 hover:bg-amber-100 transition-colors">
                            <Phone size={10} /> Llamar
                          </a>
                        )}
                        {guardia.lat && guardia.lon && (
                          <button onClick={() => openDirections(guardia.lat, guardia.lon, 'walking')}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-xl text-[11px] font-bold bg-amber-50 text-amber-600 border border-amber-100 hover:bg-amber-100 transition-colors">
                            <Navigation size={10} /> Cómo llegar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Nearest supermarket */}
          {nearestSupermarket && (
            <button onClick={() => navigate('/supermercados')} className="card-solid p-3 sm:p-4 text-left group" data-testid="tu-barrio-supermarket">
              <div className="flex items-start gap-2.5 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                  <ShoppingCart size={16} className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[11px] sm:text-xs font-extrabold text-torre-slate truncate">Super más cercano</p>
                  </div>
                  <p className="text-xs sm:text-sm font-bold text-amber-600 mt-0.5 truncate">{nearestSupermarket.nombre}</p>
                  {!approximate && <p className="text-[11px] sm:text-[11px] text-slate-400">{nearestSupermarket.walkMin} min andando</p>}
                </div>
                <ArrowRight size={14} className="text-slate-500 group-hover:text-amber-600 shrink-0 transition-colors" />
              </div>
            </button>
          )}



          {/* Nearest ERA with free spots — only from a current Parkunload count (max 10 min):
              "free spots near you" from an old snapshot sends a driver to a full street. */}
          {eraLive && eraLive.total_available > 0 && userLocation && freshnessOf(eraLive._freshness).fresh && (() => {
            // Find nearest ERA zone with free spots
            const freeZones = (eraLive.zones || []).filter(z => z.available > 0 && z.lat && z.lon);
            if (freeZones.length === 0) return null;
            const nearest = freeZones.reduce((best, z) => {
              const d = haversineKm(userLocation.lat, userLocation.lon, z.lat, z.lon);
              return (!best || d < best.dist) ? { ...z, dist: d, walkMin: Math.max(1, Math.round(d * 1000 / 80)) } : best;
            }, null);
            if (!nearest) return null;
            return (
              <button onClick={() => navigate('/aparcamiento')} className="card-solid p-3 sm:p-4 text-left group" data-testid="tu-barrio-era">
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${nearest.status === 'high' ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                    <Car size={16} className={nearest.status === 'high' ? 'text-amber-600' : 'text-emerald-600'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-[11px] sm:text-xs font-extrabold text-torre-slate">ERA más cercana</p>
                      <span className="relative flex h-1.5 w-1.5">
                        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${nearest.status === 'high' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                        <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${nearest.status === 'high' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                      </span>
                    </div>
                    <p className={`text-xs sm:text-sm font-bold mt-0.5 truncate ${nearest.status === 'high' ? 'text-amber-600' : 'text-emerald-600'}`}>{nearest.address}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-[11px] font-bold ${nearest.status === 'high' ? 'text-amber-500' : 'text-emerald-500'}`}>{nearest.available}/{nearest.capacity} libres</span>
                      {!approximate && <span className="text-[11px] text-slate-400">{nearest.walkMin} min</span>}
                      <span className="text-[11px] text-slate-400">{nearest.code}</span>
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-slate-500 group-hover:text-torre-teal shrink-0 transition-colors mt-1" />
                </div>
              </button>
            );
          })()}

          {/* Nearest bins */}
          {nearestBins?.any && (
            <button onClick={() => navigate('/residuos')} className="card-solid p-3 sm:p-4 text-left group" data-testid="tu-barrio-bins">
              <div className="flex items-start gap-2.5 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                  <Recycle size={16} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] sm:text-xs font-extrabold text-torre-slate">Contenedor más cercano</p>
                  <p className="text-xs sm:text-sm font-bold text-emerald-600 mt-0.5">{approximate ? `En ${userLocation?.barrio || 'Centro'}` : `${nearestBins.any.walk_min} min andando`}</p>
                  <div className="flex gap-1.5 mt-1">
                    {[['paper', '#3b82f6', 'P'], ['glass', '#22c55e', 'V'], ['plastic', '#eab308', 'E'], ['general', '#166534', 'R']].map(([t, color, label]) =>
                      nearestBins[t] ? (
                        <div key={t} className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold" style={{ background: color }} title={approximate ? undefined : `${nearestBins[t].walk_min} min`}>
                          {label}
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
                <ArrowRight size={14} className="text-slate-500 group-hover:text-emerald-600 shrink-0 transition-colors" />
              </div>
            </button>
          )}

        </div>
      )}

      {/* Compact local stats pills */}
      {userLocation && (
        <div className="flex flex-wrap gap-2 mt-3">
          {stats?.solicitudes_abiertas > 0 && (
            <button onClick={() => navigate('/incidencias')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-50 border border-red-200/50 text-xs font-bold text-red-600 hover:bg-red-100 transition-colors" data-testid="tu-barrio-incidencias-pill">
              <AlertTriangle size={11} /> {stats.solicitudes_abiertas} incidencia{stats.solicitudes_abiertas > 1 ? 's' : ''} activa{stats.solicitudes_abiertas > 1 ? 's' : ''}
            </button>
          )}
          {/* stats.comercios counts the old user-submitted shops collection, not the ~800-shop directory, so no number */}
          {stats?.comercios > 0 && (
            <button onClick={() => navigate('/comercio')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200/50 text-xs font-bold text-amber-700 hover:bg-amber-100 transition-colors" data-testid="tu-barrio-comercios">
              <Store size={11} /> Comercios cerca
            </button>
          )}
        </div>
      )}

      {/* Notification nudge for non-registered users */}
      {!user && userLocation && (
        <button onClick={() => onShowAuth?.()}
          className="card-solid p-3 mt-2 flex items-center gap-2.5 text-left hover:border-torre-teal/30 transition-all"
          data-testid="tu-barrio-register-nudge">
          <div className="w-8 h-8 rounded-lg bg-torre-teal/10 flex items-center justify-center shrink-0">
            <Bell size={14} className="text-torre-teal" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold text-torre-slate">Recibe alertas de tu barrio</p>
            <p className="text-[11px] text-slate-400">Tráfico, obras, eventos — personaliza qué te notificamos</p>
          </div>
          <ArrowRight size={12} className="text-torre-teal shrink-0" />
        </button>
      )}

    </div>
  );
}
